import gql from 'graphql-tag';

export const UserDetails = gql`
  fragment UserDetails on Account {
    id
    email_address
    username
    name
    phone_number
    organization_name
    slug
    ref_id
  }
`;

export const UserImages = gql`
  fragment UserImages on Account {
    logo_image {
      id
      url
      width
      height
    }
    hero_image {
      id
      url
    }
  }
`;

export const UserDetailsWithImages = gql`
  fragment UserDetailsWithImages on Account {
    ...UserDetails
    ...UserImages
  }
  ${UserDetails}
  ${UserImages}
`;

export const WatermarkConfigDetails = gql`
  fragment WatermarkConfigDetails on AccountProSettings {
    faceblock_mode
    faceblock_custom_icon_asset_image {
      id
      original_filename
      url
      width
      height
    }
    matched_photo_watermark_mode
    wallpaper_watermark_settings {
      mode
      text {
        custom_text
        font_size
      }
      image {
        asset_image {
          id
          url
        }
      }
    }
  }
`;

export const AccountAddressFields = gql`
  fragment AccountAddressFields on AccountAddress {
    id
    name
    address_line_1
    address_line_2
    city
    state
    post_code
    country {
      id
      name
      iso3
    }
    soft_deleted
  }
`;

export const AccountAlbumFolderSubscribed = gql`
  fragment AccountAlbumFolderSubscribed on Account {
    id
    subscribed_album_folder_identities(
      album_folder_ids: $albumFolderIds
      slice: { limit: 999 }
      claimed_status_filter: CLAIMED
    ) {
      records {
        id
        album_folder {
          id
        }
        identity {
          id
          name
          last_name
          has_outdated_selfie
          relationship
          current_photo {
            id
            thumbnail_urls {
              url
              width
              height
              size_code
            }
          }
        }
      }
    }
  }
`;

export const AccountSelected = gql`
  fragment AccountSelected on Account {
    id
    selected_identities(
      album_folder_ids: $albumFolderIds
      slice: { limit: 999 }
    ) {
      records {
        id
        album_membership {
          id
          album {
            id
          }
        }
        has_purchased_album_identity
        identity {
          id
          name
          last_name
          has_outdated_selfie
          relationship
          current_photo {
            id
            thumbnail_urls {
              url
              width
              height
              size_code
            }
          }
        }
      }
    }
  }
`;

export const AvatarPhoto = gql`
  fragment AvatarPhoto on Account {
    avatar_photo {
      id
      thumbnail_urls {
        size_code
        url
        width
        height
      }
    }
  }
`;

export const AccountSearchSubscriptionFeaturesDetails = gql`
  fragment AccountSearchSubscriptionFeaturesDetails on Account {
    id
    active_search_subscription_features {
      free_joins_enabled
      qr_code_enabled
      marketing_prints_enabled
      white_label_marketing_prints_enabled
      photo_watermark_enabled
      slideshow_enabled
      video_album_enabled
      members_view_enabled
      custom_tags_enabled
      face_highlights_enabled
      prints_discount_enabled
      custom_tags_enabled
    }
    active_search_subscription {
      id
      plan
    }
  }
`;

export const AccountSearchCreditDetails = gql`
  fragment AccountSearchCreditDetails on Account {
    id
    email_address
    search_subscriptions(slice: { limit: 1 }, active_only: true) {
      total
      records {
        id
        plan
        starts_at
        expires_at
        billing_period
        payment_processor
        cancelled_at
        trial_days
      }
    }
    search_subscription_free_trial_enabled
    search_subscription_discount_percentage
    can_create_free_event_album_folder
    default_payment_method {
      id
      brand
      last_4
      expiry_month
      expiry_year
    }
    storage(type: ALL) {
      used
      max
    }
    photo_storage: storage(type: PHOTO) {
      used
      max
    }
    video_storage: storage(type: VIDEO) {
      used
      max
    }
    album_folders {
      total
    }
    ...AccountSearchSubscriptionFeaturesDetails
  }
  ${AccountSearchSubscriptionFeaturesDetails}
`;

export const AccountPhotoManagerSubscriptionDetails = gql`
  fragment AccountPhotoManagerSubscriptionDetails on Account {
    id
    photo_manager_subscription_free_trial_enabled
    photo_manager_enabled
    photo_manager_subscriptions(active_only: true) {
      total
      records {
        id
        billing_period
        starts_at
        expires_at
        cancelled_at
        is_active
        trial_days
      }
    }
    pro_settings {
      has_free_photo_manager
      photo_finder_credits_waived
    }
    photo_finder_photos_count
    photo_finder_photos_count_pending_charge: photo_finder_photos_count(
      pending_charge_only: true
    )
    photo_finder_credits_balance {
      total
    }
  }
`;

export const AccountPhotoFinderSubscriptionDetails = gql`
  fragment AccountPhotoFinderSubscriptionDetails on Account {
    id
    photo_finder_subscriptions(active_only: true) {
      total
      records {
        id
        billing_period
        starts_at
        expires_at
        cancelled_at
        is_active
        subscription_item {
          max_photos
          price {
            value
            currency
          }
        }
      }
    }
    photo_finder_photos_count
    photo_finder_photos_count_pending_charge: photo_finder_photos_count(
      pending_charge_only: true
    )
    photo_finder_credits_balance {
      total
    }
  }
`;

export const UserDefaultSettings = gql`
  fragment UserDefaultSettings on Account {
    default_settings {
      hash_code
      watermark {
        asset_image {
          id
        }
        placement
      }
    }
  }
`;

export const AccountPhotoAttributionPage = gql`
  fragment AccountPhotoAttributionPage on AccountPhotoAttributionPageSettings {
    is_enabled
    description
    contact_phone
    email_address
    instagram_url
    hero_asset_image {
      id
      url
    }
  }
`;
export const UserProSettings = gql`
  fragment UserProSettings on Account {
    photo_upload_terms_accepted
    pro_settings {
      has_free_photo_manager
      photo_finder_credits_waived
      default_pro_role
      default_album_delivery_mode
      sell_photos_enabled
      share_photos_enabled
      broadcasts_enabled
      custom_broadcasts_enabled
      high_volume_event_addons_enabled
      requires_stripe_connect_setup
      multipart_uploads_enabled
      max_multipart_upload_bytes
      album_photo_groups_enabled
      multi_pose_print_packages_enabled
      multi_identity_print_packages_enabled
      multi_identity_digital_bundles_enabled
      print_order_services_enabled
      skip_gallery_link_prompt
      owner_purchased_joins_enabled
      matched_photos_download_enabled
      default_currency
      photo_search_enabled
      pro_directory {
        is_enabled
        description
        asset_image {
          id
          url
        }
      }
      locked_print_partner {
        id
        name
      }
      default_print_partner {
        id
        name
      }
      requires_print_partner_setup
      photo_attribution_page {
        ...AccountPhotoAttributionPage
      }
      ...WatermarkConfigDetails
    }
  }
  ${WatermarkConfigDetails}
  ${AccountPhotoAttributionPage}
`;

export const UserNotificationSettings = gql`
  fragment UserNotificationSettings on Account {
    notification_group_settings {
      notifications_enabled
      notification_group
    }

    notification_settings {
      category
      email_enabled
      push_enabled
      sms_enabled
    }

    match_notification_settings: notification_settings(category: MATCH) {
      category
      email_enabled
      push_enabled
      sms_enabled
    }

    has_push_token
  }
`;

export const UserTaxDetails = gql`
  fragment UserTaxDetails on Account {
    taxes {
      id
      tax_type
      jurisdiction
      state {
        id
        name
        abbreviation
      }
      tax_percentage
      tax_product_category
    }
  }
`;

export const VendorDetails = gql`
  fragment VendorDetails on Account {
    id
    organization_name
    email_address
    logo_image {
      id
      url
    }
    pro_settings {
      ...WatermarkConfigDetails
      multi_pose_print_packages_enabled
      multi_identity_print_packages_enabled
      multi_identity_digital_bundles_enabled
      sell_prints_enabled
      default_currency
      photo_attribution_page {
        email_address
      }
    }
  }
  ${WatermarkConfigDetails}
`;

export const AccountReportDetails = gql`
  fragment AccountReportDetails on AccountReport {
    id
    type
    csv_download_url
    created_at
  }
`;
